(() => {
  'use strict';
  if (window.PuaneProductGalleryLoaded) return;
  window.PuaneProductGalleryLoaded = true;

  let modal;
  let activeRoot;
  let items = [];
  let activeIndex = 0;
  let zoom = 1;
  let offsetX = 0;
  let offsetY = 0;
  let pointerStart = null;
  let returnFocus = null;

  const pathOf = (url) => {
    try { return decodeURIComponent(new URL(url, window.location.origin).pathname); }
    catch (_) { return String(url || '').split('?')[0]; }
  };

  const thumbUrl = (thumb) => thumb?.dataset.imageUrl || thumb?.querySelector('img')?.currentSrc || thumb?.querySelector('img')?.src || '';
  const visibleThumbs = (root) => {
    const all = [...root.querySelectorAll('[data-pb2b-thumb]')].filter((thumb) => thumbUrl(thumb));
    const visible = all.filter((thumb) => !thumb.hidden && thumb.getAttribute('aria-hidden') !== 'true');
    const seen = new Set();
    return (visible.length ? visible : all).filter((thumb) => {
      const key = pathOf(thumbUrl(thumb));
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const mainImage = (root) => root?.querySelector('.pb2b__hero-image');
  const setMain = (root, thumb) => {
    const image = mainImage(root);
    const url = thumbUrl(thumb);
    if (!image || !url) return;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = url;
    root.querySelectorAll('[data-pb2b-thumb]').forEach((item) => item.classList.toggle('is-active', item === thumb));
  };

  const applyTransform = () => {
    if (!modal) return;
    const image = modal.querySelector('.puane-gallery-modal__image');
    image.style.transform = `translate3d(${offsetX}px,${offsetY}px,0) scale(${zoom})`;
    modal.classList.toggle('is-zoomed', zoom > 1.01);
    modal.querySelector('[data-gallery-zoom-out]').disabled = zoom <= 1.01;
    modal.querySelector('[data-gallery-reset]').disabled = zoom <= 1.01;
  };

  const setZoom = (next) => {
    zoom = Math.min(3, Math.max(1, next));
    if (zoom <= 1.01) { zoom = 1; offsetX = 0; offsetY = 0; }
    applyTransform();
  };

  const ensureModal = () => {
    if (modal?.isConnected) return modal;
    modal = document.createElement('div');
    modal.className = 'puane-gallery-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Ürün görselleri');
    modal.innerHTML = `
      <div class="puane-gallery-modal__stage">
        <img class="puane-gallery-modal__image" alt="Ürün görseli" draggable="false">
        <button type="button" class="puane-gallery-modal__close" aria-label="Kapat">×</button>
        <div class="puane-gallery-modal__tools" aria-label="Yakınlaştırma araçları">
          <button type="button" class="puane-gallery-modal__tool" data-gallery-zoom-out aria-label="Uzaklaştır">−</button>
          <button type="button" class="puane-gallery-modal__tool" data-gallery-reset aria-label="Yakınlaştırmayı sıfırla">1:1</button>
          <button type="button" class="puane-gallery-modal__tool" data-gallery-zoom-in aria-label="Yakınlaştır">+</button>
        </div>
        <button type="button" class="puane-gallery-modal__nav puane-gallery-modal__prev" aria-label="Önceki görsel">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 4L7 12l8 8"></path>
          </svg>
        </button>
        <button type="button" class="puane-gallery-modal__nav puane-gallery-modal__next" aria-label="Sonraki görsel">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 4l8 8-8 8"></path>
          </svg>
        </button>
        <div class="puane-gallery-modal__counter" aria-live="polite"></div>
      </div>
      <div class="puane-gallery-modal__thumbs" aria-label="Görsel seçenekleri"></div>`;
    document.body.appendChild(modal);

    modal.querySelector('.puane-gallery-modal__close').addEventListener('click', close);
    modal.querySelector('.puane-gallery-modal__prev').addEventListener('click', () => show(activeIndex - 1));
    modal.querySelector('.puane-gallery-modal__next').addEventListener('click', () => show(activeIndex + 1));
    modal.querySelector('[data-gallery-zoom-in]').addEventListener('click', () => setZoom(zoom + .5));
    modal.querySelector('[data-gallery-zoom-out]').addEventListener('click', () => setZoom(zoom - .5));
    modal.querySelector('[data-gallery-reset]').addEventListener('click', () => setZoom(1));

    const stage = modal.querySelector('.puane-gallery-modal__stage');
    stage.addEventListener('dblclick', (event) => { event.preventDefault(); setZoom(zoom > 1 ? 1 : 2.5); });
    stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      setZoom(zoom + (event.deltaY < 0 ? .25 : -.25));
    }, { passive: false });
    stage.addEventListener('pointerdown', (event) => {
      if (event.target.closest('button')) return;
      pointerStart = { x: event.clientX, y: event.clientY, offsetX, offsetY };
      stage.setPointerCapture?.(event.pointerId);
      modal.classList.add('is-dragging');
    });
    stage.addEventListener('pointermove', (event) => {
      if (!pointerStart || zoom <= 1) return;
      offsetX = pointerStart.offsetX + event.clientX - pointerStart.x;
      offsetY = pointerStart.offsetY + event.clientY - pointerStart.y;
      applyTransform();
    });
    stage.addEventListener('pointerup', (event) => {
      if (pointerStart && zoom <= 1) {
        const distance = event.clientX - pointerStart.x;
        if (Math.abs(distance) > 60 && items.length > 1) show(activeIndex + (distance < 0 ? 1 : -1));
      }
      pointerStart = null;
      modal.classList.remove('is-dragging');
    });
    stage.addEventListener('pointercancel', () => { pointerStart = null; modal.classList.remove('is-dragging'); });
    return modal;
  };

  const renderThumbs = () => {
    const strip = ensureModal().querySelector('.puane-gallery-modal__thumbs');
    strip.innerHTML = '';
    items.forEach((thumb, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `puane-gallery-modal__thumb${index === activeIndex ? ' is-active' : ''}`;
      button.setAttribute('aria-label', `${index + 1}. görsel`);
      const image = document.createElement('img');
      image.src = thumb.querySelector('img')?.currentSrc || thumb.querySelector('img')?.src || thumbUrl(thumb);
      image.alt = '';
      button.appendChild(image);
      button.addEventListener('click', () => show(index));
      strip.appendChild(button);
    });
  };

  const show = (index) => {
    if (!items.length) return;
    activeIndex = (index + items.length) % items.length;
    const selected = items[activeIndex];
    const image = ensureModal().querySelector('.puane-gallery-modal__image');
    image.removeAttribute('srcset');
    image.src = thumbUrl(selected);
    image.alt = mainImage(activeRoot)?.alt || 'Ürün görseli';
    setMain(activeRoot, selected);
    setZoom(1);
    modal.classList.toggle('has-multiple', items.length > 1);
    modal.querySelector('.puane-gallery-modal__counter').textContent = `${activeIndex + 1} / ${items.length}`;
    renderThumbs();
  };

  const open = (root, trigger) => {
    activeRoot = root;
    returnFocus = trigger;
    items = visibleThumbs(root);
    const current = pathOf(mainImage(root)?.currentSrc || mainImage(root)?.src || '');
    activeIndex = items.findIndex((thumb) => pathOf(thumbUrl(thumb)) === current);
    if (activeIndex < 0) activeIndex = items.findIndex((thumb) => thumb.classList.contains('is-active'));
    if (activeIndex < 0) activeIndex = 0;
    if (!items.length && mainImage(root)?.src) {
      const virtual = document.createElement('button');
      virtual.dataset.imageUrl = mainImage(root).src;
      items = [virtual];
    }
    if (!items.length) return;
    ensureModal().classList.add('is-open');
    document.documentElement.classList.add('puane-gallery-lock');
    show(activeIndex);
    modal.querySelector('.puane-gallery-modal__close').focus({ preventScroll: true });
  };

  function close() {
    if (!modal) return;
    modal.classList.remove('is-open', 'is-zoomed', 'is-dragging');
    document.documentElement.classList.remove('puane-gallery-lock');
    setZoom(1);
    returnFocus?.focus?.({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-puane-product-gallery] [data-pb2b-thumb]');
    if (thumb) {
      const root = thumb.closest('[data-puane-product-gallery]');
      setMain(root, thumb);
      return;
    }
    const trigger = event.target.closest('[data-puane-product-gallery] [data-puane-gallery-open]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger.closest('[data-puane-product-gallery]'), trigger);
  });

  document.addEventListener('keydown', (event) => {
    if (!modal?.classList.contains('is-open')) return;
    if (event.key === 'Escape') close();
    else if (event.key === 'ArrowLeft' && items.length > 1) show(activeIndex - 1);
    else if (event.key === 'ArrowRight' && items.length > 1) show(activeIndex + 1);
    else if (event.key === '+' || event.key === '=') setZoom(zoom + .5);
    else if (event.key === '-') setZoom(zoom - .5);
    else if (event.key === '0') setZoom(1);
  });
})();